import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { defaults } from 'es-toolkit/compat';

import clsx from 'clsx';

import { ScrollDepthOptions, scrollToChild, scrollToDepth } from '../utils/scroll';
import BaseComponent from './BaseComponent';

interface Props {
  children?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
  horizontal?: boolean;
  id?: string;
  onScroll?(event: TouchEvent | WheelEvent): void;
  vertical?: boolean;
  passive?: boolean;
  removeCompositeLayer?: boolean; // this toggles a css class that does not use a transform: translateZ(0) css hack
}

export default class ScrollLockComponent extends BaseComponent<Props> {
  static defaultProps: Partial<Props> = {
    'data-testid': undefined,
    horizontal: false,
    vertical: true,
    passive: false,
    removeCompositeLayer: false,
  };

  parentDOMNode = React.createRef<HTMLDivElement>();
  allowUp: boolean;
  allowDown: boolean;
  lastY: number;
  offsetTop: number;

  constructor(props: Props) {
    super(props);
  }

  getParentDOMNode() {
    return this.parentDOMNode.current;
  }

  componentDidMount() {
    this.handleLockScroll();
  }

  componentWillUnmount() {
    this.handleUnlockScroll();
  }

  handleLockScroll() {
    const { current } = this.parentDOMNode;
    const { passive } = this.props;
    if (!current) {
      return;
    }

    if (this.props.vertical) {
      current.addEventListener('scroll', this.handleScroll, { passive });
    }

    if (this.props.horizontal) {
      current.addEventListener('scroll', this.handleHorizontalScroll, {
        passive,
      });
    }
  }

  handleUnlockScroll() {
    const { current } = this.parentDOMNode;

    if (!current) {
      return;
    }

    if (this.props.vertical) {
      current.removeEventListener('scroll', this.handleScroll);
    }

    if (this.props.horizontal) {
      current.removeEventListener('scroll', this.handleHorizontalScroll);
    }
  }

  handleCancelScroll(event: Event) {
    event.returnValue = false;
    return false;
  }

  handleScroll(event: Event) {
    const { current } = this.parentDOMNode;
    if (!current) {
      return false;
    }
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (typeof this.props.onScroll === 'function') {
      this.props.onScroll(event as WheelEvent);
    }
    return true;
  }

  handleHorizontalScroll(event: Event) {
    const { current } = this.parentDOMNode;
    if (!current) {
      return false;
    }
    event.stopPropagation();
    event.stopImmediatePropagation();

    return true;
  }

  getScrollDepth(): number {
    return this.parentDOMNode.current?.scrollTop ?? 0;
  }

  getScrollX(): number {
    return this.parentDOMNode.current?.scrollLeft ?? 0;
  }

  isNearBottom(margin = 0) {
    const { current } = this.parentDOMNode;

    if (!current) {
      return false;
    }

    // http://stackoverflow.com/questions/876115/how-can-i-determine-if-a-div-is-scrolled-to-the-bottom
    return current.scrollHeight - current.offsetHeight - current.scrollTop <= margin;
  }

  isNearTop(margin = 0) {
    const { current } = this.parentDOMNode;

    if (!current) {
      return false;
    }

    return current.scrollTop <= margin;
  }

  isNodeHiddenAbove(ref: HTMLElement) {
    const node = ReactDOM.findDOMNode(ref) as HTMLElement;
    const { current } = this.parentDOMNode;
    if (!node) {
      return true;
    }

    if (!current) {
      return false;
    }

    return current.scrollTop > node.offsetTop;
  }

  isNodeHiddenBelow(ref: HTMLElement) {
    const node = ReactDOM.findDOMNode(ref) as HTMLElement;
    const { current } = this.parentDOMNode;
    if (!node) {
      return true;
    }

    if (!current) {
      return false;
    }

    return node.offsetTop + node.offsetHeight > current.scrollTop + current.clientHeight;
  }

  isNodeHiddenToTheLeft(ref: HTMLElement) {
    const node = ReactDOM.findDOMNode(ref) as HTMLElement;
    const { current } = this.parentDOMNode;
    if (!node) {
      return true;
    }

    if (!current) {
      return false;
    }

    return current.scrollLeft > node.offsetLeft;
  }

  isNodeHiddenToTheRight(ref: HTMLElement) {
    const node = ReactDOM.findDOMNode(ref) as HTMLElement;
    const { current } = this.parentDOMNode;
    if (!node) {
      return true;
    }

    if (!current) {
      return false;
    }

    return node.offsetLeft + node.offsetWidth > current.scrollLeft + current.clientWidth;
  }

  isNodeVisible(ref: HTMLElement, options?: { horizontal: boolean }) {
    const node = ReactDOM.findDOMNode(ref) as HTMLElement;
    if (!node) {
      return false;
    }

    if (options && options.horizontal) {
      // Same logic as below but horizontally
      return !(this.isNodeHiddenToTheLeft(node) || this.isNodeHiddenToTheRight(node));
    } else {
      // If the top of the node is above the current scroll position, or if the
      // bottom of the node is below the bottom of the container, set the
      // container's scroll position to the top of the node
      // See also: https://developer.mozilla.org/en-US/docs/Web/API/Element
      // See also: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement
      return !(this.isNodeHiddenAbove(node) || this.isNodeHiddenBelow(node));
    }
  }

  scrollTo(ref: HTMLElement, options: ScrollDepthOptions = {}) {
    const { current } = this.parentDOMNode;
    if (!ref) {
      return;
    }

    const defaultOptions = {
      minimumScrollTime: 0,
      maximumScrollTime: 0,
    };

    const actualOptions = defaults(options, defaultOptions);

    let node: HTMLElement;
    try {
      node = ReactDOM.findDOMNode(ref) as HTMLElement;
    } catch (_error) {
      // `ReactDOM.findDOMNode throws if the ref is unmounted. If that's the case,
      // it means someone used a settimeout to scroll to something that then became
      // unmounted. Do nothing.
      return;
    }
    const isNodeVisible = this.isNodeVisible(ref, {
      horizontal: !!actualOptions.horizontal,
    });

    if (!isNodeVisible && current) {
      scrollToChild(current, node, actualOptions);
    }
  }

  scrollHorizontallyTo(ref: HTMLElement, options: ScrollDepthOptions = {}) {
    this.scrollTo(ref, {
      ...options,
      horizontal: true,
    });
  }

  scrollToDepth(scrollDepth?: number, options: ScrollDepthOptions = {}) {
    const { current } = this.parentDOMNode;
    if (scrollDepth == null) {
      return;
    }

    const defaultOptions = {
      minimumScrollTime: 0,
      maximumScrollTime: 0,
    };

    const actualOptions = defaults(options, defaultOptions);
    scrollToDepth(current, scrollDepth, actualOptions);
  }

  scrollToXPosition(scrollX: number, options: ScrollDepthOptions = {}) {
    this.scrollToDepth(scrollX, {
      ...options,
      horizontal: true,
    });
  }

  /**
   * Use native scrollTo to scroll horizontally to the given x position.
   * Safari does not support { behavior: 'smooth' } and will instead jump to the x position.
   * A polyfill for smooth scrolling has been applied to dashboard and consumer.
   */
  smoothScrollToXPosition(scrollX: number) {
    if (this.parentDOMNode.current) {
      this.parentDOMNode.current.scrollTo({ behavior: 'smooth', left: scrollX });
    }
  }

  scrollToTop() {
    const { current } = this.parentDOMNode;

    if (current) {
      current.scrollTop = 0;
    }
  }

  scrollToBottom() {
    const { current } = this.parentDOMNode;
    if (current) {
      current.scrollTop = current.scrollHeight;
    }
  }

  render() {
    const {
      children,
      className: classNameFromProps,
      'data-testid': dataTestId,
      id,
      removeCompositeLayer,
    } = this.props;
    const scrollLockClass = removeCompositeLayer ? 'ScrollLock-layer' : 'ScrollLock';
    const className = clsx(scrollLockClass, classNameFromProps);

    return (
      <div className={className} data-testid={dataTestId} id={id} ref={this.parentDOMNode}>
        {children}
      </div>
    );
  }
}
